import { TFunction } from "i18next";
export type StatName =
  | "total_points"
  | "event_points"
  | "now_cost"
  | "selected_by_percent"
  | "minutes"
  | "goals_scored"
  | "assists"
  | "clean_sheets"
  | "goals_conceded"
  | "own_goals"
  | "penalties_saved"
  | "penalties_missed"
  | "yellow_cards"
  | "red_cards"
  | "saves"
  | "bonus"
  | "bps"
  | "influence"
  | "creativity"
  | "threat"
  | "ict_index"
  | "form"
  | "dreamteam_count"
  | "value_form"
  | "value_season"
  | "points_per_game"
  | "transfers_in"
  | "transfers_out"
  | "transfers_in_event"
  | "transfers_out_event"
  | "cost_change_start"
  | "cost_change_start_fall"
  | "cost_change_event"
  | "cost_change_event_fall"
  | "expected_assists"
  | "expected_goals"
  | "expected_goal_involvements"
  | "expected_goals_conceded"
  | "starts"
  | "tackles"
  | "clearances_blocks_interceptions"
  | "recoveries"
  | "defensive_contribution";

export interface IStatDetails {
  key: StatName;
  label: string;
  description: string;
  shortName: string;
}

// TODO: Get these changes reviewed by MS/PL
export const statDetails = (t: TFunction): Record<StatName, IStatDetails> => ({
  total_points: {
    description: t(
      "statistics:stats.totalPoints.description",
      "Total points earned this season.",
    ),
    key: "total_points",
    label: t("statistics:stats.totalPoints.label", "Total points"),
    shortName: t("statistics:stats.totalPoints.shortName", "TP"),
  },
  event_points: {
    description: t(
      "statistics:stats.eventPoints.description",
      "Total points earned in the latest Gameweek.",
    ),
    key: "event_points",
    label: t("statistics:stats.eventPoints.label", "Gameweek points"),
    shortName: t("statistics:stats.eventPoints.shortName", "GWP"),
  },
  now_cost: {
    description: t(
      "statistics:stats.nowCost.description",
      "Current buying price in the transfer market.",
    ),
    key: "now_cost",
    label: t("statistics:stats.nowCost.label", "Price"),
    shortName: t("statistics:stats.nowCost.shortName", "£"),
  },
  selected_by_percent: {
    description: t(
      "statistics:stats.selectedByPercent.description",
      "The percentage of overall Fantasy managers who currently own the player.",
    ),
    key: "selected_by_percent",
    label: t("statistics:stats.selectedByPercent.label", "Teams selected by %"),
    shortName: t("statistics:stats.selectedByPercent.shortName", "TSB"),
  },
  minutes: {
    description: t(
      "statistics:stats.minutes.description",
      "Total minutes played this season.",
    ),
    key: "minutes",
    label: t("statistics:stats.minutes.label", "Minutes played"),
    shortName: t("statistics:stats.minutes.shortName", "MP"),
  },
  goals_scored: {
    description: t(
      "statistics:stats.goalsScored.description",
      "Total goals scored this season.",
    ),
    key: "goals_scored",
    label: t("statistics:stats.goalsScored.label", "Goals scored"),
    shortName: t("statistics:stats.goalsScored.shortName", "GS"),
  },
  assists: {
    description: t(
      "statistics:stats.assists.description",
      "Total goal assists - awarded to the player from the goalscoring team who makes the final pass before a goal is scored, including own goals.",
    ),
    key: "assists",
    label: t("statistics:stats.assists.label", "Assists"),
    shortName: t("statistics:stats.assists.shortName", "A"),
  },
  clean_sheets: {
    description: t(
      "statistics:stats.cleanSheets.description",
      "Total clean sheets - awarded to players who do not concede a goal and have played at least 60 minutes.",
    ),
    key: "clean_sheets",
    label: t("statistics:stats.cleanSheets.label", "Clean sheets"),
    shortName: t("statistics:stats.cleanSheets.shortName", "CS"),
  },
  goals_conceded: {
    description: t(
      "statistics:stats.goalsConceded.description",
      "Total number of goals conceded by a team while the player has been on the pitch.",
    ),
    key: "goals_conceded",
    label: t("statistics:stats.goalsConceded.label", "Goals conceded"),
    shortName: t("statistics:stats.goalsConceded.shortName", "GC"),
  },
  own_goals: {
    description: t(
      "statistics:stats.ownGoals.description",
      "Awarded to a player who puts the ball into his own team's goal.",
    ),
    key: "own_goals",
    label: t("statistics:stats.ownGoals.label", "Own goals"),
    shortName: t("statistics:stats.ownGoals.shortName", "OG"),
  },
  penalties_saved: {
    description: t(
      "statistics:stats.penaltiesSaved.description",
      "Awarded to a goalkeeper who touches the ball when saving a penalty.",
    ),
    key: "penalties_saved",
    label: t("statistics:stats.penaltiesSaved.label", "Penalties saved"),
    shortName: t("statistics:stats.penaltiesSaved.shortName", "PS"),
  },
  penalties_missed: {
    description: t(
      "statistics:stats.penaltiesMissed.description",
      "Awarded to a player who takes a penalty but does not score from the penalty spot.",
    ),
    key: "penalties_missed",
    label: t("statistics:stats.penaltiesMissed.label", "Penalties missed"),
    shortName: t("statistics:stats.penaltiesMissed.shortName", "PM"),
  },
  yellow_cards: {
    description: t(
      "statistics:stats.yellowCards.description",
      "Total yellow cards this season.",
    ),
    key: "yellow_cards",
    label: t("statistics:stats.yellowCards.label", "Yellow cards"),
    shortName: t("statistics:stats.yellowCards.shortName", "YC"),
  },
  red_cards: {
    description: t(
      "statistics:stats.redCards.description",
      "Total red cards this season.",
    ),
    key: "red_cards",
    label: t("statistics:stats.redCards.label", "Red cards"),
    shortName: t("statistics:stats.redCards.shortName", "RC"),
  },
  saves: {
    description: t(
      "statistics:stats.saves.description",
      "Total times a goalkeeper has saved a shot on goal.",
    ),
    key: "saves",
    label: t("statistics:stats.saves.label", "Saves"),
    shortName: t("statistics:stats.saves.shortName", "S"),
  },
  bonus: {
    description: t(
      "statistics:stats.bonus.description",
      "The three best performing players in each match according to the BPS will receive additional bonus points - 3 points will be awarded to the highest scoring player, 2 to the second best and 1 to the third.",
    ),
    key: "bonus",
    label: t("statistics:stats.bonus.label", "Bonus points"),
    shortName: t("statistics:stats.bonus.shortName", "B"),
  },
  bps: {
    description: t(
      "statistics:stats.bps.description",
      "The Bonus Points System (BPS) uses a range of stats to create a BPS score for each player. The three best performing players in each match will be awarded bonus points.",
    ),
    key: "bps",
    label: t("statistics:stats.bps.label", "Bonus Points System"),
    shortName: t("statistics:stats.bps.shortName", "BPS"),
  },
  influence: {
    description: t(
      "statistics:stats.influence.description",
      "Influence evaluates a player's impact on a match, taking into account actions that could directly or indirectly affect the match outcome. Part of the ICT index.",
    ),
    key: "influence",
    label: t("statistics:stats.influence.label", "Influence"),
    shortName: t("statistics:stats.influence.shortName", "I"),
  },
  creativity: {
    description: t(
      "statistics:stats.creativity.description",
      "Creativity assesses player performance in terms of producing goalscoring opportunities for other players. Part of the ICT index.",
    ),
    key: "creativity",
    label: t("statistics:stats.creativity.label", "Creativity"),
    shortName: t("statistics:stats.creativity.shortName", "C"),
  },
  threat: {
    description: t(
      "statistics:stats.threat.description",
      "Threat gauges players who are most likely to score goals. Part of the ICT index.",
    ),
    key: "threat",
    label: t("statistics:stats.threat.label", "Threat"),
    shortName: t("statistics:stats.threat.shortName", "T"),
  },
  ict_index: {
    description: t(
      "statistics:stats.ictIndex.description",
      "Statistical index developed specifically to assess a player as an FPL asset, combining Influence, Creativity and Threat scores.",
    ),
    key: "ict_index",
    label: t("statistics:stats.ictIndex.label", "ICT Index"),
    shortName: t("statistics:stats.ictIndex.shortName", "ICT"),
  },
  form: {
    description: t(
      "statistics:stats.form.description",
      "Form is a player's average score per match, calculated from all matches played by his club in the last 30 days.",
    ),
    key: "form",
    label: t("statistics:stats.form.label", "Form"),
    shortName: t("statistics:stats.form.shortName", "F"),
  },
  dreamteam_count: {
    description: t(
      "statistics:stats.dreamteamCount.description",
      "The number of times a player has been selected in a Gameweek Dream Team. Players with the most points in a Gameweek in a valid formation earn a place in the Dream Team.",
    ),
    key: "dreamteam_count",
    label: t("statistics:stats.dreamteamCount.label", "Times in Dream Team"),
    shortName: t("statistics:stats.dreamteamCount.shortName", "DT"),
  },
  value_form: {
    description: t(
      "statistics:stats.valueForm.description",
      "Player's form divided by player's value.",
    ),
    key: "value_form",
    label: t("statistics:stats.valueForm.label", "Value (form)"),
    shortName: t("statistics:stats.valueForm.shortName", "V(F)"),
  },
  value_season: {
    description: t(
      "statistics:stats.valueSeason.description",
      "Player's total points divided by player's value.",
    ),
    key: "value_season",
    label: t("statistics:stats.valueSeason.label", "Value (season)"),
    shortName: t("statistics:stats.valueSeason.shortName", "V(S)"),
  },
  points_per_game: {
    description: t(
      "statistics:stats.pointsPerGame.description",
      "Player's total points divided by player's number of matches.",
    ),
    key: "points_per_game",
    label: t("statistics:stats.pointsPerGame.label", "Points per match"),
    shortName: t("statistics:stats.pointsPerGame.shortName", "PPM"),
  },
  transfers_in: {
    description: t(
      "statistics:stats.transfersIn.description",
      "Total number of times a player has been transferred in to a team this season.",
    ),
    key: "transfers_in",
    label: t("statistics:stats.transfersIn.label", "Transfers in"),
    shortName: t("statistics:stats.transfersIn.shortName", "TI"),
  },
  transfers_out: {
    description: t(
      "statistics:stats.transfersOut.description",
      "Total number of times a player has been transferred out of a team this season.",
    ),
    key: "transfers_out",
    label: t("statistics:stats.transfersOut.label", "Transfers out"),
    shortName: t("statistics:stats.transfersOut.shortName", "TO"),
  },
  transfers_in_event: {
    description: t(
      "statistics:stats.transfersInEvent.description",
      "Total number of times a player has been transferred in to a team this Gameweek.",
    ),
    key: "transfers_in_event",
    label: t(
      "statistics:stats.transfersInEvent.label",
      "Transfers in (Gameweek)",
    ),
    shortName: t("statistics:stats.transfersInEvent.shortName", "TI(GW)"),
  },
  transfers_out_event: {
    description: t(
      "statistics:stats.transfersOutEvent.description",
      "Total number of times a player has been transferred out of a team this Gameweek.",
    ),
    key: "transfers_out_event",
    label: t(
      "statistics:stats.transfersOutEvent.label",
      "Transfers out (Gameweek)",
    ),
    shortName: t("statistics:stats.transfersOutEvent.shortName", "TO(GW)"),
  },
  cost_change_start: {
    description: t(
      "statistics:stats.costChangeStart.description",
      "How much a player's price has increased since the start of the season.",
    ),
    key: "cost_change_start",
    label: t("statistics:stats.costChangeStart.label", "Price rise"),
    shortName: t("statistics:stats.costChangeStart.shortName", "PR"),
  },
  cost_change_start_fall: {
    description: t(
      "statistics:stats.costChangeStartFall.description",
      "How much a player price has fallen since the start of the season.",
    ),
    key: "cost_change_start_fall",
    label: t("statistics:stats.costChangeStartFall.label", "Price fall"),
    shortName: t("statistics:stats.costChangeStartFall.shortName", "PF"),
  },
  cost_change_event: {
    description: t(
      "statistics:stats.costChangeEvent.description",
      "How much a player's price has increased this Gameweek.",
    ),
    key: "cost_change_event",
    label: t("statistics:stats.costChangeEvent.label", "Price rise (Gameweek)"),
    shortName: t("statistics:stats.costChangeEvent.shortName", "PR(GW)"),
  },
  cost_change_event_fall: {
    description: t(
      "statistics:stats.costChangeEventFall.description",
      "How much a player's price has fallen this Gameweek.",
    ),
    key: "cost_change_event_fall",
    label: t(
      "statistics:stats.costChangeEventFall.label",
      "Price fall (Gameweek)",
    ),
    shortName: t("statistics:stats.costChangeEventFall.shortName", "PF(GW)"),
  },
  expected_assists: {
    description: t(
      "statistics:stats.expectedAssists.description",
      "Expected assists since the start of the season",
    ),
    key: "expected_assists",
    label: t(
      "statistics:stats.expectedAssists.label",
      "Expected assists (Total)",
    ),
    shortName: t("statistics:stats.expectedAssists.shortName", "xA(T)"),
  },
  expected_goals: {
    description: t(
      "statistics:stats.expectedGoals.description",
      "Expected goals since the start of the season",
    ),
    key: "expected_goals",
    label: t("statistics:stats.expectedGoals.label", "Expected goals (Total)"),
    shortName: t("statistics:stats.expectedGoals.shortName", "xG(T)"),
  },
  expected_goal_involvements: {
    description: t(
      "statistics:stats.expectedGoalInvolvements.description",
      "Expected goal involvements since the start of the season",
    ),
    key: "expected_goal_involvements",
    label: t(
      "statistics:stats.expectedGoalInvolvements.label",
      "Expected goal involvements (Total)",
    ),
    shortName: t(
      "statistics:stats.expectedGoalInvolvements.shortName",
      "xGI(T)",
    ),
  },
  expected_goals_conceded: {
    description: t(
      "statistics:stats.expectedGoalsConceded.description",
      "Expected goals conceded since the start of the season",
    ),
    key: "expected_goals_conceded",
    label: t(
      "statistics:stats.expectedGoalsConceded.label",
      "Expected goals conceded (Total)",
    ),
    shortName: t("statistics:stats.expectedGoalsConceded.shortName", "xGC(T)"),
  },
  starts: {
    description: t(
      "statistics:stats.starts.description",
      "Total games started this season",
    ),
    key: "starts",
    label: t("statistics:stats.starts.label", "Starts"),
    shortName: t("statistics:stats.starts.shortName", "ST"),
  },
  tackles: {
    description: t(
      "statistics:stats.tackles.description",
      "Total tackles this season.",
    ),
    key: "tackles",
    label: t("statistics:stats.tackles.label", "Tackles"),
    shortName: t("statistics:stats.tackles.shortName", "TKL"),
  },
  clearances_blocks_interceptions: {
    description: t(
      "statistics:stats.clearancesBlocksInterceptions.description",
      "Total clearances blocks and interceptions (CBI) this season.",
    ),
    key: "clearances_blocks_interceptions",
    label: t(
      "statistics:stats.clearancesBlocksInterceptions.label",
      "Clearances Blocks and Interceptions",
    ),
    shortName: t(
      "statistics:stats.clearancesBlocksInterceptions.shortName",
      "CBI",
    ),
  },
  recoveries: {
    description: t(
      "statistics:stats.recoveries.description",
      "Total recoveries this season.",
    ),
    key: "recoveries",
    label: t("statistics:stats.recoveries.label", "Recoveries"),
    shortName: t("statistics:stats.recoveries.shortName", "REC"),
  },
  defensive_contribution: {
    description: t(
      "statistics:stats.defensiveContribution.description",
      "Points gained from cumulative clearances, blocks and interceptions.",
    ),
    key: "defensive_contribution",
    label: t(
      "statistics:stats.defensiveContribution.label",
      "Defensive Contributions",
    ),
    shortName: t("statistics:stats.defensiveContribution.shortName", "DC"),
  },
});

export const isStatName = (name: string): name is StatName =>
  name in statDetails(((k: string) => k) as unknown as TFunction);

/**
 * Retrieves stat details for a given stat name with optional overrides.
 *
 * @param name - The stat name to retrieve details for (must be a valid StatName)
 * @param overrides - Optional overrides for label and/or shortName. Useful when you need
 *                    context-specific display text while keeping the key and description
 *                    from the central registry.
 *
 * @returns The stat details object with any overrides applied, or null if the stat name is invalid
 *
 * @example
 * // Get standard stat details
 * const nowCost = getStatDetails("now_cost");
 * // Returns: { key: "now_cost", label: "Price", shortName: "£", description: "..." }
 *
 * @example
 * // Override label and shortName for context-specific display
 * const currentPrice = getStatDetails("now_cost", {
 *   label: "Current Price",
 *   shortName: "CP"
 * });
 * // Returns: { key: "now_cost", label: "Current Price", shortName: "CP", description: "..." }
 */
export const getStatDetails = (
  t: TFunction,
  name: StatName,
  overrides?: Partial<Omit<IStatDetails, "key" | "description">>,
): IStatDetails | null => {
  const details = statDetails(t)[name];
  if (!details) return null;

  return {
    ...details,
    ...overrides,
  };
};

export const getStatFormattedLabel = (t: TFunction, statName: StatName) => {
  const stat = getStatDetails(t, statName);

  if (!stat) return "**";

  return (
    stat.shortName ??
    stat.label
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
  );
};
